import { formatMoney } from '../../lib/utils'
import { mesAno } from '../../lib/ajustesImportacao'
import {
  arred, erroDaParte, parteAnteriorDe, podeMoverParaAnterior, temAnterior,
  type EscolhaPagamento,
} from '../../lib/pagamentosImportacao'
import type { DestinoPagamento, ItemPreVisualizacaoCsv, ResumoPagamentos } from '../../types'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'

const OPCOES: { valor: DestinoPagamento; rotulo: string }[] = [
  { valor: 'quitaAnterior', rotulo: 'Quita a fatura anterior' },
  { valor: 'reduzEsta', rotulo: 'Reduz esta fatura' },
  { valor: 'divide', rotulo: 'Divide' },
]

/** Dentro da célula da descrição de um "Pagamento recebido": onde o pagamento entra (anterior, esta ou dividido). */
export function PagamentoDoItem({ item, escolha, onEscolha, travado }: {
  item: ItemPreVisualizacaoCsv
  escolha: EscolhaPagamento
  onEscolha: (e: EscolhaPagamento) => void
  /** Linha desmarcada: não será importada, então a escolha fica só como leitura. */
  travado: boolean
}) {
  const s = item.sugestaoPagamento!
  const anterior = mesAno(s.faturaAnteriorMes, s.faturaAnteriorAno)
  const semAnterior = !temAnterior(item)
  const valor = Math.abs(item.valor)
  const erro = travado ? null : erroDaParte(item, escolha)
  const parte = parteAnteriorDe(item, escolha, s.saldoRestante)
  const depois = arred(s.saldoRestante - parte)
  const idBase = `pg-${item.indice}`
  return (
    <div className="mt-2 space-y-1.5 rounded-md border border-line p-2 text-2xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-white">Para onde vai este pagamento?</span>
        {s.forte && <Badge variant="income" className="normal-case">Sugestão forte</Badge>}
      </div>
      {semAnterior ? (
        <p className="text-muted">Não há fatura anterior em aberto para receber o pagamento: ele reduz esta fatura.</p>
      ) : (
        <>
          <div role="radiogroup" aria-label="Destino do pagamento" className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:gap-3">
            {OPCOES.map((o) => (
              <label key={o.valor} className="flex items-center gap-1.5 text-white">
                <input
                  type="radio" name={idBase} checked={escolha.destino === o.valor} disabled={travado}
                  onChange={() => onEscolha({ ...escolha, destino: o.valor })}
                />
                {o.valor === 'quitaAnterior' ? `Quita a fatura de ${anterior}` : o.rotulo}
              </label>
            ))}
          </div>
          {escolha.destino === 'divide' && (
            <label className="flex flex-wrap items-center gap-2 text-white">
              Parte que vai para a fatura de {anterior}:
              <input
                type="number" step="0.01" min={0.01} inputMode="decimal" aria-label="Valor da parte da fatura anterior" aria-invalid={!!erro}
                className="form-control w-28 text-right" value={escolha.parte} disabled={travado}
                onChange={(e) => onEscolha({ ...escolha, parte: e.target.value })}
              />
              <span className="text-muted">e {formatMoney(Math.max(valor - (Number(escolha.parte) || 0), 0))} nesta fatura</span>
            </label>
          )}
          {erro && <p role="alert" className="text-expense">{erro}</p>}
          <p className="text-muted">
            Fatura de {anterior}: faltam <strong className="text-white">{formatMoney(s.saldoRestante)}</strong>
            {'; '}depois deste pagamento: <strong className={depois < 0 ? 'text-warning' : 'text-white'}>{formatMoney(depois)}</strong>.
          </p>
          {s.motivo && <p className="text-muted">{s.motivo}</p>}
        </>
      )}
    </div>
  )
}

/** Linha que já foi gravada na fatura errada: botão para mover para a anterior (vai no `mover` do confirmar). */
export function MoverPagamentoParaAnterior({ item, marcado, onMarcar, desabilitado }: {
  item: ItemPreVisualizacaoCsv
  marcado: boolean
  onMarcar: (m: boolean) => void
  desabilitado?: boolean
}) {
  if (!podeMoverParaAnterior(item)) return null
  const s = item.sugestaoPagamento!
  const anterior = mesAno(s.faturaAnteriorMes, s.faturaAnteriorAno)
  return (
    <div className="mt-2 space-y-1 rounded-md border border-warning/40 bg-warning/10 p-2 text-2xs">
      <p className="text-warning">
        Este pagamento já está lançado em outra fatura{s.forte ? ' e bate com o que falta na anterior' : ''}. Provavelmente ele deveria estar na fatura de {anterior}.
      </p>
      <Button
        type="button" size="sm" variant={marcado ? 'primary' : 'light'} icon={marcado ? 'ri-check-line' : 'ri-arrow-go-back-line'}
        aria-pressed={marcado} disabled={desabilitado} onClick={() => onMarcar(!marcado)}
      >
        {marcado ? `Será movido para ${anterior}` : `Mover para a fatura anterior (${anterior})`}
      </Button>
    </div>
  )
}

/** Rodapé: quanto falta na fatura anterior, quanto foi marcado para ela e o aviso (não bloqueia) de excesso. */
export function ResumoPagamentosAnterior({ resumo }: { resumo: ResumoPagamentos | null }) {
  if (!resumo || !resumo.faturaAnteriorId) return null
  const anterior = mesAno(resumo.faturaAnteriorMes, resumo.faturaAnteriorAno)
  return (
    <div className="space-y-1 rounded-lg border border-line p-3 text-13">
      <p className="font-medium text-white">Pagamentos recebidos e a fatura anterior ({anterior})</p>
      <dl className="grid grid-cols-2 gap-2">
        <div><dt className="text-2xs text-muted">Falta pagar na anterior</dt><dd className="font-semibold text-white">{formatMoney(resumo.saldoAnteriorRestante)}</dd></div>
        <div><dt className="text-2xs text-muted">Marcado para a anterior</dt><dd className="font-semibold text-white">{formatMoney(resumo.totalMarcadoParaAnterior)}</dd></div>
      </dl>
      {resumo.excedeSaldo && (
        <p role="status" className="text-12 text-warning">
          Você marcou {formatMoney(resumo.excedente)} a mais do que falta na fatura de {anterior}. Dá para importar assim, mas ela ficará com saldo negativo (crédito).
        </p>
      )}
    </div>
  )
}
